import { z } from 'zod';
import { CLINIC_SECTIONS } from './access.js';
import {
  AmountRialSchema,
  DateOnlySchema,
  IsoDateTimeSchema,
  RialStringSchema,
  UuidSchema,
  optionalText,
  pageSchema,
} from './common.js';
import { FinanceEffectSchema, PositiveRialSchema } from './finance.js';
import { InventoryEffectSchema } from './inventory.js';
import { GENDERS } from './patients.js';

/** Area codes for structured treatment records (ADR-018). Persian labels live in the web app. */
export const TREATMENT_AREAS = [
  // face — toxin
  'forehead',
  'glabella',
  'crows_feet_right',
  'crows_feet_left',
  'bunny_lines',
  'lip_flip',
  'chin',
  'masseter_right',
  'masseter_left',
  'neck',
  // face — filler
  'lips_upper',
  'lips_lower',
  'nasolabial_right',
  'nasolabial_left',
  'marionette_right',
  'marionette_left',
  'cheek_right',
  'cheek_left',
  'tear_trough_right',
  'tear_trough_left',
  'jawline_right',
  'jawline_left',
  'temple_right',
  'temple_left',
  // body — laser
  'face_full',
  'upper_lip',
  'underarm',
  'arms_full',
  'forearms',
  'legs_full',
  'legs_lower',
  'bikini',
  'back',
  'abdomen',
  'chest',
] as const;
export type TreatmentArea = (typeof TREATMENT_AREAS)[number];

const FieldKeySchema = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/);
const LabelSchema = z.string().min(1).max(80);

const AreaMapFieldSchema = z.object({
  type: z.literal('area_map'),
  key: FieldKeySchema,
  label: LabelSchema,
  unit: z.enum(['units', 'ml']),
  areas: z.array(z.enum(TREATMENT_AREAS)).min(1),
  max: z.number().positive(),
  required: z.boolean(),
});
/** Drawings a map can be placed on. Point coordinates are fractions of the drawing's box. */
export const INJECTION_DIAGRAMS = ['face_front'] as const;
export type InjectionDiagram = (typeof INJECTION_DIAGRAMS)[number];

export const INJECTION_POINT_LABEL_MAX = 40;

/**
 * ADR-028: the standard areas of `area_map` plus points the doctor places freely on the diagram.
 * `area_map` stays for template versions written before it (BR-29).
 */
const InjectionMapFieldSchema = z.object({
  type: z.literal('injection_map'),
  key: FieldKeySchema,
  label: LabelSchema,
  unit: z.enum(['units', 'ml']),
  diagram: z.enum(INJECTION_DIAGRAMS),
  areas: z.array(z.enum(TREATMENT_AREAS)).min(1),
  /** Upper bound of one area's or one point's amount. */
  max: z.number().positive(),
  maxPoints: z.number().int().min(0).max(100),
  required: z.boolean(),
});
export type InjectionMapField = z.infer<typeof InjectionMapFieldSchema>;

/** A fraction of the diagram's width or height, kept to three decimals. */
const CoordinateSchema = z
  .number()
  .min(0)
  .max(1)
  .transform((v) => Math.round(v * 1000) / 1000);

const injectionPointSchema = (amount: z.ZodType<number>) =>
  z.strictObject({
    label: z
      .string()
      .trim()
      .min(1, { message: 'نام نقطه را بنویسید.' })
      .max(INJECTION_POINT_LABEL_MAX, { message: `نام نقطه حداکثر ${INJECTION_POINT_LABEL_MAX} نویسه است.` }),
    x: CoordinateSchema,
    y: CoordinateSchema,
    amount,
  });

/**
 * The value of an `injection_map` field, without a template: every known area and any positive
 * amount. Responses use it; input is checked again with `injectionMapSchemaFor` of the field.
 */
export const InjectionMapValueSchema = z.strictObject({
  areas: z.partialRecord(z.enum(TREATMENT_AREAS), z.number().positive()).default({}),
  points: z.array(injectionPointSchema(z.number().positive())).max(100).default([]),
});
export type InjectionMapValue = z.infer<typeof InjectionMapValueSchema>;
export type InjectionPoint = InjectionMapValue['points'][number];

/** The strict schema of one `injection_map` field: its areas, its maximum, its point limit. */
export function injectionMapSchemaFor(field: InjectionMapField) {
  const amount = z.number().positive().max(field.max);
  return z.strictObject({
    // partialRecord: in Zod 4 a record over an enum otherwise requires every key.
    areas: z.partialRecord(z.enum(field.areas as [TreatmentArea, ...TreatmentArea[]]), amount).default({}),
    points: z
      .array(injectionPointSchema(amount))
      .max(field.maxPoints, { message: `حداکثر ${field.maxPoints} نقطه‌ی دلخواه ثبت می‌شود.` })
      .default([]),
  });
}

export function injectionMapCount(value: InjectionMapValue): number {
  return Object.keys(value.areas).length + value.points.length;
}

/** Sum of every area and point (what BR-28 compares with the recorded consumption). */
export function injectionMapTotal(value: InjectionMapValue): number {
  const sum =
    Object.values(value.areas).reduce((s, n) => s + n, 0) + value.points.reduce((s, p) => s + p.amount, 0);
  // Amounts are decimals like 0.1 ml; keep the sum free of binary noise.
  return Math.round(sum * 1000) / 1000;
}

const AreaSelectFieldSchema = z.object({
  type: z.literal('area_select'),
  key: FieldKeySchema,
  label: LabelSchema,
  areas: z.array(z.enum(TREATMENT_AREAS)).min(1),
  required: z.boolean(),
});
const NumberFieldSchema = z.object({
  type: z.literal('number'),
  key: FieldKeySchema,
  label: LabelSchema,
  unit: z.string().max(20).nullable(),
  min: z.number(),
  max: z.number(),
  required: z.boolean(),
});
const SelectFieldSchema = z.object({
  type: z.literal('select'),
  key: FieldKeySchema,
  label: LabelSchema,
  options: z.array(z.object({ value: FieldKeySchema, label: LabelSchema })).min(1),
  required: z.boolean(),
});
const TextFieldSchema = z.object({
  type: z.literal('text'),
  key: FieldKeySchema,
  label: LabelSchema,
  maxLength: z.number().int().min(1).max(2000),
  required: z.boolean(),
});

export const TemplateFieldSchema = z.discriminatedUnion('type', [
  AreaMapFieldSchema,
  InjectionMapFieldSchema,
  AreaSelectFieldSchema,
  NumberFieldSchema,
  SelectFieldSchema,
  TextFieldSchema,
]);
export type TemplateField = z.infer<typeof TemplateFieldSchema>;

export const TemplateDefinitionSchema = z.object({
  fields: z
    .array(TemplateFieldSchema)
    .min(1)
    .max(20)
    .refine((f) => new Set(f.map((x) => x.key)).size === f.length, { message: 'کلید تکراری است.' }),
});
export type TemplateDefinition = z.infer<typeof TemplateDefinitionSchema>;

/**
 * Builds the strict schema of `details` for one template version. The server validates with it
 * (never trusting the client) and the web builds its form from the same definition.
 */
export function detailsSchemaFor(definition: TemplateDefinition) {
  const shape: Record<string, z.ZodType> = {};
  for (const f of definition.fields) {
    let s: z.ZodType;
    switch (f.type) {
      case 'area_map': {
        const areas = z.enum(f.areas as [TreatmentArea, ...TreatmentArea[]]);
        const amount = z.number().positive().max(f.max);
        // partialRecord: in Zod 4 a record over an enum otherwise requires every key.
        s = z
          .partialRecord(areas, amount)
          .refine((v) => !f.required || Object.keys(v).length > 0, { message: 'دست‌کم یک ناحیه ثبت کنید.' });
        break;
      }
      case 'injection_map':
        s = injectionMapSchemaFor(f).refine((v) => !f.required || injectionMapCount(v) > 0, {
          message: 'دست‌کم یک ناحیه یا نقطه ثبت کنید.',
        });
        break;
      case 'area_select': {
        const areas = z.enum(f.areas as [TreatmentArea, ...TreatmentArea[]]);
        s = z
          .array(areas)
          .refine((v) => new Set(v).size === v.length, { message: 'ناحیه‌ی تکراری است.' })
          .refine((v) => !f.required || v.length > 0, { message: 'دست‌کم یک ناحیه انتخاب کنید.' });
        break;
      }
      case 'number':
        s = z.number().min(f.min).max(f.max);
        break;
      case 'select':
        s = z.enum(f.options.map((o) => o.value) as [string, ...string[]]);
        break;
      case 'text':
        s = z.string().trim().min(f.required ? 1 : 0).max(f.maxLength);
        break;
    }
    shape[f.key] = f.required ? s : s.optional();
  }
  return z.strictObject(shape);
}

export const TreatmentTemplateSchema = z.object({
  id: UuidSchema,
  key: z.string().nullable(),
  name: z.string(),
  isSystem: z.boolean(),
  version: z.object({
    id: UuidSchema,
    version: z.number().int(),
    definition: TemplateDefinitionSchema,
  }),
});
export type TreatmentTemplate = z.infer<typeof TreatmentTemplateSchema>;
export const TreatmentTemplateListResponseSchema = z.object({ items: z.array(TreatmentTemplateSchema) });

export const VISIT_STATUSES = ['open', 'completed', 'void'] as const;
export type VisitStatus = (typeof VISIT_STATUSES)[number];

export const VisitItemDetailsSchema = z.object({
  templateVersionId: UuidSchema,
  values: z.record(z.string(), z.unknown()),
});
export type VisitItemDetails = z.infer<typeof VisitItemDetailsSchema>;

/**
 * A stored treatment record as it is read back: with the definition of the template version it
 * was written with, so an old record is always shown in its own terms (BR-29).
 */
export const VisitItemDetailsViewSchema = VisitItemDetailsSchema.extend({
  templateId: UuidSchema,
  definition: TemplateDefinitionSchema,
});
export type VisitItemDetailsView = z.infer<typeof VisitItemDetailsViewSchema>;

export const VisitItemSchema = z.object({
  id: UuidSchema,
  serviceId: UuidSchema,
  serviceName: z.string(),
  performedByMembershipId: UuidSchema,
  priceRial: RialStringSchema,
  /** When the client is due for this service again, and for its touch-up (ADR-052); set at completion. */
  recallOn: DateOnlySchema.nullable(),
  touchUpOn: DateOnlySchema.nullable(),
  /** Clinical; present only for users with `medical.read` (ADR-026). */
  details: VisitItemDetailsViewSchema.nullable().optional(),
  createdAt: IsoDateTimeSchema,
});
export type VisitItem = z.infer<typeof VisitItemSchema>;

/** The two dates of a service line (ADR-052): null is «none». */
export const FollowUpDatesSchema = z.object({
  recallOn: DateOnlySchema.nullable(),
  touchUpOn: DateOnlySchema.nullable(),
});
export type FollowUpDatesInput = z.input<typeof FollowUpDatesSchema>;

export const VisitSchema = z.object({
  id: UuidSchema,
  patientId: UuidSchema,
  appointmentId: UuidSchema.nullable(),
  practitionerMembershipId: UuidSchema,
  /** From the appointment, or chosen for a visit without one (ADR-036); every line is of this section. */
  section: z.enum(CLINIC_SECTIONS),
  status: z.enum(VISIT_STATUSES),
  startedAt: IsoDateTimeSchema,
  completedAt: IsoDateTimeSchema.nullable(),
  voidedAt: IsoDateTimeSchema.nullable(),
  voidReason: z.string().nullable(),
  items: z.array(VisitItemSchema),
  totalRial: RialStringSchema,
  /** The discount given on the visit ("0" when none or outside the caller's till). */
  discountRial: RialStringSchema,
});
export type Visit = z.infer<typeof VisitSchema>;

export const VisitListQuerySchema = z.object({
  patientId: UuidSchema.optional(),
  /** The visit of one appointment (at most one). */
  appointmentId: UuidSchema.optional(),
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const VisitListResponseSchema = pageSchema(VisitSchema);
export type VisitListResponse = z.infer<typeof VisitListResponseSchema>;

/**
 * Each patient's last completed (not void) visit, for a list or a booking: when, by whom and which
 * services. No treatment record, so reading it is not an audited clinical read.
 */
export const LatestVisitSchema = z.object({
  patientId: UuidSchema,
  visitId: UuidSchema,
  section: z.enum(CLINIC_SECTIONS),
  practitionerMembershipId: UuidSchema,
  completedAt: IsoDateTimeSchema,
  /** The services of its lines, in line order, without repeats. */
  serviceIds: z.array(UuidSchema),
});
export type LatestVisit = z.infer<typeof LatestVisitSchema>;
export const LatestVisitsResponseSchema = z.object({ items: z.array(LatestVisitSchema) });
export type LatestVisitsResponse = z.infer<typeof LatestVisitsResponseSchema>;

/** Without a section: the clinic part for a doctor, laser for a laser operator (ADR-036). */
export const WalkInVisitInputSchema = z.object({
  patientId: UuidSchema,
  section: z.enum(CLINIC_SECTIONS).optional(),
});

export const VisitItemInputSchema = z.object({
  serviceId: UuidSchema,
  /** Defaults to the service price; the snapshot is frozen when the visit completes. */
  priceRial: AmountRialSchema.optional(),
  details: VisitItemDetailsSchema.nullish().transform((v) => v ?? null),
});
export type VisitItemInput = z.input<typeof VisitItemInputSchema>;

export const VisitItemUpdateInputSchema = z.object({
  priceRial: AmountRialSchema.optional(),
  /** Replaces the treatment record; omit to keep it. */
  details: VisitItemDetailsSchema.optional(),
});
export type VisitItemUpdateInput = z.input<typeof VisitItemUpdateInputSchema>;

export const VisitVoidInputSchema = z.object({
  reason: z.string().trim().min(3, { message: 'دلیل ابطال را بنویسید.' }).max(300),
});

/** ADR-016 principle 4: what completing the visit will do, computed by the server. */
export const CompletePreviewSchema = z.object({
  record: z.array(z.object({ serviceName: z.string(), hasDetails: z.boolean() })),
  inventory: InventoryEffectSchema,
  finance: FinanceEffectSchema,
  /**
   * The dates each line will get unless changed (ADR-052): renewal from the service's days, touch-up
   * from the practitioner's days for a touch-up service, none when this looks like that touch-up.
   */
  followUps: z.array(
    z.object({
      itemId: UuidSchema,
      serviceName: z.string(),
      touchUpService: z.boolean(),
      recallOn: DateOnlySchema.nullable(),
      touchUpOn: DateOnlySchema.nullable(),
    }),
  ),
});
export type CompletePreview = z.infer<typeof CompletePreviewSchema>;

/**
 * Profit of each service of a visit (ADR-053), apart from the books (BR-11): price − its share of the
 * discount − its materials at their purchase price. Materials not tied to a line, and the discount, are
 * shared by price. Only with `finance.read`.
 */
const ProfitFiguresSchema = z.object({
  priceRial: RialStringSchema,
  discountRial: RialStringSchema,
  materialCostRial: RialStringSchema,
  profitRial: RialStringSchema,
  /** A material without a purchase price yet: its cost is left out until the price is given. */
  costIncomplete: z.boolean(),
});
export const VisitProfitSchema = z.object({
  visitId: UuidSchema,
  lines: z.array(ProfitFiguresSchema.extend({ itemId: UuidSchema, serviceId: UuidSchema, serviceName: z.string() })),
  total: ProfitFiguresSchema,
});
export type VisitProfit = z.infer<typeof VisitProfitSchema>;
export const VisitProfitQuerySchema = z.object({ discountRial: PositiveRialSchema.optional() });

/** The services report's profit columns (ADR-053): completed visits in the range, by service. */
export const ServiceCostReportSchema = z.object({
  from: DateOnlySchema,
  to: DateOnlySchema,
  section: z.enum(CLINIC_SECTIONS).nullable(),
  items: z.array(
    z.object({
      serviceId: UuidSchema,
      discountRial: RialStringSchema,
      materialCostRial: RialStringSchema,
      /** The services report's revenue − discount − materials. */
      profitRial: RialStringSchema,
      costIncomplete: z.boolean(),
    }),
  ),
});
export type ServiceCostReport = z.infer<typeof ServiceCostReportSchema>;

// ---------------------------------------------------------------------------------------------
// Treatment methods (ADR-028): a doctor's own named map, one variant per patient gender.
// ---------------------------------------------------------------------------------------------

const MethodNameSchema = z
  .string()
  .trim()
  .min(1, { message: 'نام متد را بنویسید.' })
  .max(80, { message: 'نام متد حداکثر ۸۰ نویسه است.' });

const HttpsUrlSchema = z.url({ protocol: /^https$/ });

/** Only https links: the address is shown to the doctor and opened in a new tab. */
const ReferenceUrlSchema = z
  .string()
  .trim()
  .max(500, { message: 'نشانی حداکثر ۵۰۰ نویسه است.' })
  .nullish()
  .transform((v) => (v ? v : null))
  .refine((v) => v === null || HttpsUrlSchema.safeParse(v).success, {
    // No «://» in the text: inside a right-to-left sentence it renders back to front.
    message: 'نشانی باید کامل باشد و با https شروع شود.',
  });

export const TreatmentMethodSchema = z.object({
  id: UuidSchema,
  templateId: UuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  referenceUrl: z.string().nullable(),
  /** true for a method suggested by the platform (read-only). */
  isSystem: z.boolean(),
  variants: z.object({
    female: InjectionMapValueSchema.nullable(),
    male: InjectionMapValueSchema.nullable(),
  }),
  updatedAt: IsoDateTimeSchema,
});
export type TreatmentMethod = z.infer<typeof TreatmentMethodSchema>;

export const TreatmentMethodListQuerySchema = z.object({ templateId: UuidSchema.optional() });
export const TreatmentMethodListResponseSchema = z.object({ items: z.array(TreatmentMethodSchema) });
export type TreatmentMethodListResponse = z.infer<typeof TreatmentMethodListResponseSchema>;

export const TreatmentMethodCreateInputSchema = z.object({
  templateId: UuidSchema,
  name: MethodNameSchema,
  description: optionalText(2000),
  referenceUrl: ReferenceUrlSchema,
  /** Which variant `map` is: the gender of the patient it was recorded for. */
  gender: z.enum(GENDERS),
  map: InjectionMapValueSchema,
});
export type TreatmentMethodCreateInput = z.input<typeof TreatmentMethodCreateInputSchema>;

export const TreatmentMethodUpdateInputSchema = z.object({
  name: MethodNameSchema,
  description: optionalText(2000),
  referenceUrl: ReferenceUrlSchema,
});
export type TreatmentMethodUpdateInput = z.input<typeof TreatmentMethodUpdateInputSchema>;

export const TreatmentMethodVariantInputSchema = z.object({ map: InjectionMapValueSchema });
export type TreatmentMethodVariantInput = z.input<typeof TreatmentMethodVariantInputSchema>;

export const GenderParamSchema = z.enum(GENDERS);

/** An optional discount given when the visit is completed (employer 2026-10-07); body may be absent. */
export const VisitCompleteInputSchema = z.object({
  discountRial: PositiveRialSchema.nullish().transform((v) => v ?? null),
  /** Dates chosen per line (ADR-052); a line left out gets the preview's suggestion. */
  followUps: z
    .array(FollowUpDatesSchema.extend({ itemId: UuidSchema }))
    .max(50)
    .nullish()
    .transform((v) => v ?? null),
});
export type VisitCompleteInput = z.input<typeof VisitCompleteInputSchema>;
export const VisitCompletePreviewQuerySchema = z.object({ discountRial: PositiveRialSchema.optional() });

// ---------------------------------------------------------------------------------------------
// Past services (ADR-052): a service the client had before, recorded only for its renewal and
// touch-up dates. No money, stock or clinical record; voided, never deleted.
// ---------------------------------------------------------------------------------------------

export const PriorServiceSchema = z.object({
  id: UuidSchema,
  patientId: UuidSchema,
  serviceId: UuidSchema,
  serviceName: z.string(),
  section: z.enum(CLINIC_SECTIONS),
  performedOn: DateOnlySchema,
  /** A practitioner of this clinic, or null: done outside the clinic. */
  performedByMembershipId: UuidSchema.nullable(),
  note: z.string().nullable(),
  recallOn: DateOnlySchema.nullable(),
  touchUpOn: DateOnlySchema.nullable(),
  voidedAt: IsoDateTimeSchema.nullable(),
  voidReason: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type PriorService = z.infer<typeof PriorServiceSchema>;
export const PriorServiceListResponseSchema = z.object({ items: z.array(PriorServiceSchema) });

export const PriorServiceInputSchema = z.object({
  serviceId: UuidSchema,
  /** Not after today (checked by the server, in Tehran). */
  performedOn: DateOnlySchema,
  performedByMembershipId: UuidSchema.nullish().transform((v) => v ?? null),
  note: optionalText(500),
  recallOn: DateOnlySchema.nullish().transform((v) => v ?? null),
  touchUpOn: DateOnlySchema.nullish().transform((v) => v ?? null),
});
export type PriorServiceInput = z.input<typeof PriorServiceInputSchema>;

export const PriorServiceVoidInputSchema = z.object({
  reason: z.string().trim().min(3, { message: 'دلیل ابطال را بنویسید.' }).max(300),
});
